import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { useLoaderData, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DownloadIcon from "@mui/icons-material/Download";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import PersonIcon from "@mui/icons-material/Person";
import VerifiedIcon from "@mui/icons-material/Verified";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { wrapFormikHandleChange } from "../../../../../infrastructure/utils/formatters";
import {
  COMPANY_NATIONAL_ID_FORMAT_MSG,
  COMPANY_NATIONAL_ID_HINT_MSG,
  COMPANY_NATIONAL_ID_LENGTH,
  companyNationalIdInputProps,
  validateCompanyNationalId,
} from "../../../../../infrastructure/utils/companyNationalIdRules";
import { isValidIranianNationalCode } from "../../../../../infrastructure/utils/nationalCodeRules";

import ContractorRegistrationApiService from "../../../../../infrastructure/api/contractorRegistration";
import DefaultApiService from "../../../../../infrastructure/api/default";
import {
  normalizeIban,
  onlyDigits,
  validateBankAccount,
  validateCardNumber,
  validateIban,
} from "../../../contractor-registration/contractorBankFields";
import {
  buildContractorRegistrationDetailsSchema,
  fa2enDigits,
} from "../../../contractor-registration/contractorRegistrationDetailsSchema";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import DropdownPicker from "../../../../components/shared/dropdown";
import JalaliDatePicker from "../../../../components/shared/jalali-datepicker";
import {
  formatLocalDateForApi,
  toLocalDateInputValue,
} from "../../../../middleware/middleware";

const REG_STATUS = {
  Pending: 1,
  UnderReview: 2,
  Approved: 3,
  Rejected: 4,
  NeedsMoreInfo: 5,
  DocumentsApproved: 6,
};

const statusOptions = [
  { value: 1, label: "در انتظار بررسی", buttonLabel: "شروع بررسی", color: "warning" },
  { value: 2, label: "بررسی مدارک / در حال بررسی", buttonLabel: "ارسال به بررسی مدارک", color: "info" },
  { value: 3, label: "تایید نهایی (صدور قرارداد)", buttonLabel: "تایید نهایی", color: "success" },
  { value: 4, label: "رد شده", buttonLabel: "رد درخواست", color: "error" },
  { value: 5, label: "نیاز به تکمیل مدارک", buttonLabel: "درخواست تکمیل مدارک", color: "secondary" },
  { value: 6, label: "تایید اولیه (ایجاد اکانت)", buttonLabel: "تایید اولیه", color: "primary" },
];

const statusColors = {
  1: "warning",
  2: "info",
  3: "success",
  4: "error",
  5: "secondary",
  6: "primary",
};

/**
 * فقط انتقال‌های مجاز مدیر — برای جلوگیری از خطای انسانی در دراپ‌داون.
 * جریان Phase 7.7.1: Pending → Initial(6) / Review(2) → NeedsMoreInfo(5) ↔ Review → Final(3)
 */
const STATUS_TRANSITIONS = {
  [REG_STATUS.Pending]: [
    REG_STATUS.UnderReview,
    REG_STATUS.DocumentsApproved,
    REG_STATUS.NeedsMoreInfo,
    REG_STATUS.Rejected,
  ],
  [REG_STATUS.UnderReview]: [
    REG_STATUS.DocumentsApproved,
    REG_STATUS.Approved,
    REG_STATUS.NeedsMoreInfo,
    REG_STATUS.Rejected,
  ],
  [REG_STATUS.DocumentsApproved]: [
    REG_STATUS.NeedsMoreInfo,
    REG_STATUS.UnderReview,
    REG_STATUS.Approved,
    REG_STATUS.Rejected,
  ],
  [REG_STATUS.NeedsMoreInfo]: [
    REG_STATUS.UnderReview,
    REG_STATUS.DocumentsApproved,
    REG_STATUS.Rejected,
  ],
  [REG_STATUS.Approved]: [],
  [REG_STATUS.Rejected]: [REG_STATUS.UnderReview],
};

const getStatusMeta = (value) =>
  statusOptions.find((s) => s.value === Number(value)) || {
    value: Number(value),
    label: "نامشخص",
    buttonLabel: "نامشخص",
    color: "default",
  };

const getAllowedStatusActions = (currentStatus) => {
  const next = STATUS_TRANSITIONS[Number(currentStatus)] || [];
  return next.map(getStatusMeta);
};

/** وضعیت‌هایی که اکانت پیمانکار ساخته/فعال می‌شود */
const ACCOUNT_PROVISION_STATUSES = new Set([3, 6]);
/** فقط تایید نهایی نیاز به داده کامل قرارداد دارد */
const FINAL_APPROVAL_STATUS = 3;

const validationSchema = buildContractorRegistrationDetailsSchema();

const buildContractReadiness = (values) => {
  const isLegal = values.registrationType === "legal";
  const checks = [
    { key: "identity", label: "نام و نام خانوادگی", ok: !!(values.firstName && values.lastName) },
    {
      key: "nationalCode",
      label: "کد ملی",
      ok: isValidIranianNationalCode(values.nationalCode),
    },
    {
      key: "mobile",
      label: "موبایل",
      ok: /^09\d{9}$/.test(fa2enDigits(values.mobile).replace(/\D/g, "")),
    },
    { key: "email", label: "ایمیل", ok: !!values.email?.trim() },
    {
      key: "birthDate",
      label: "تاریخ تولد",
      ok: !!values.birthDate,
    },
    {
      key: "address",
      label: "آدرس کامل",
      ok:
        (values.companyAddress || "").trim().length >= 10 &&
        (values.companyAddress || "").trim().length <= 200,
    },
    { key: "province", label: "استان", ok: !!values.province?.trim() },
    { key: "city", label: "شهر", ok: !!values.city?.trim() },
    {
      key: "postalCode",
      label: "کدپستی",
      ok: /^\d{10}$/.test(fa2enDigits(values.postalCode).replace(/\D/g, "")),
    },
    {
      key: "workExperience",
      label: "سابقه کار",
      ok: !!values.workExperience?.trim(),
    },
    { key: "iban", label: "شماره شبا", ok: !validateIban(values.ibanNumber) },
    {
      key: "account",
      label: "شماره حساب",
      ok: !validateBankAccount(values.bankAccountNumber),
    },
    {
      key: "card",
      label: "شماره کارت",
      ok: !validateCardNumber(values.cardNumber),
    },
  ];

  if (isLegal) {
    checks.push(
      {
        key: "companyName",
        label: "نام شرکت",
        ok: (values.companyName || "").trim().length >= 3,
      },
      {
        key: "companyNationalId",
        label: "شناسه ملی شرکت",
        ok: !validateCompanyNationalId(
          fa2enDigits(values.companyNationalId).replace(/\D/g, "")
        ),
      },
      {
        key: "companyReg",
        label: "شماره ثبت شرکت",
        ok: /^\d{1,6}$/.test(
          fa2enDigits(values.companyRegistrationNumber).replace(/\D/g, "")
        ),
      },
      {
        key: "companyPhone",
        label: "تلفن شرکت",
        ok: /^0\d{10}$/.test(
          fa2enDigits(values.companyPhone).replace(/\D/g, "")
        ),
      },
      {
        key: "foundedYear",
        label: "سال تاسیس",
        ok: (() => {
          const y = Number(values.foundedYear);
          return Number.isFinite(y) && y >= 1200 && y <= 1600;
        })(),
      }
    );
  }

  const missing = checks.filter((c) => !c.ok);
  return { checks, missing, ready: missing.length === 0 };
};

const toDateInputValue = (value) => toLocalDateInputValue(value);

const toPersianDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("fa-IR");
};

const normalizeArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
      return [];
    }
  }
  return [];
};

const normalizeListResponse = (response) => {
  return response?.data?.data || response?.data || response || [];
};

const toUrlString = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value[0] || "";
  if (typeof value === "object") {
    return value.url || value.path || value.value || value.fullUrl || "";
  }
  return String(value);
};

const ContractorRegistrationModifyPage = ({
  apiBase = "SLN/Management/ContractorRegistration",
  listPath = "/SLN/Management/ContractorRegistration",
  reviewNotesLabel = "یادداشت بررسی / توضیحات مدیر",
}) => {
  const data = useLoaderData("root");
  let navigate = useNavigate();
  const { id: registrationId } = useParams();

  const baseUrl = window.globalConfig?.API_URL || "";
  const [loading, setLoading] = useState(false);
  const [provinceOptions, setProvinceOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [distributionCompanies, setDistributionCompanies] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  useEffect(() => {
    let mounted = true;

    const loadLocations = async () => {
      try {
        const [provincesRes, citiesRes, companiesRes] = await Promise.all([
          ContractorRegistrationApiService.getProvinces(),
          ContractorRegistrationApiService.getCities(),
          ContractorRegistrationApiService.getDistributionCompanies(),
        ]);

        if (!mounted) return;

        const provinces = normalizeListResponse(provincesRes);
        const cities = normalizeListResponse(citiesRes);
        const companies = normalizeListResponse(companiesRes);
        setProvinceOptions(Array.isArray(provinces) ? provinces : []);
        setCityOptions(Array.isArray(cities) ? cities : []);
        setDistributionCompanies(Array.isArray(companies) ? companies : []);
      } catch (_error) {
        if (!mounted) return;
        setProvinceOptions([]);
        setCityOptions([]);
        setDistributionCompanies([]);
      }
    };

    loadLocations();
    return () => {
      mounted = false;
    };
  }, []);

  const onNavigateToListPage = () => {
    navigate(listPath);
  };

  const registration =
    data?.contractorRegistration || data?.ContractorRegistration || {};
  const operationalAccess =
    data?.operationalAccess || data?.OperationalAccess || null;
  const contractorInfoId =
    data?.contractorInfoId || data?.ContractorInfoId || null;
  const masterContractId =
    data?.masterContractId || data?.MasterContractId || null;
  const canEditDetails = true; // حتی بعد از تایید نهایی، اصلاح شناسه/آدرس برای صدور قرارداد لازم است
  const needsMasterContractIssue =
    Number(registration.status) === REG_STATUS.Approved &&
    !!contractorInfoId &&
    !masterContractId;
  const workSampleUrls = normalizeArray(registration.workSamplesUrls);
  const fullName = `${registration.firstName || ""} ${registration.lastName || ""}`.trim();
  const displayName = fullName || "-";
  const displayCompanyName = registration.companyName || "نام شرکت ثبت نشده";
  const initialProvinceId = useMemo(() => {
    if (registration.provinceId) return registration.provinceId;
    const provinceName = registration.province || "";
    const foundProvince = provinceOptions.find((item) => item.title === provinceName);
    return foundProvince?.id || "";
  }, [registration.province, registration.provinceId, provinceOptions]);

  const initialCityId = useMemo(() => {
    if (registration.cityId) return registration.cityId;
    const cityName = registration.city || "";
    const foundCity = cityOptions.find(
      (item) =>
        item.cityName === cityName &&
        (!initialProvinceId || Number(item.provinceId) === Number(initialProvinceId))
    );
    return foundCity?.id || "";
  }, [registration.city, registration.cityId, cityOptions, initialProvinceId]);

  // Submit Action
  const onSubmit = async (formValue, { setSubmitting }) => {
    setLoading(true);
    const isLegalRegistration =
      formValue.registrationType === "legal" ||
      registration.registrationType === "legal";
    const finalStatus = parseInt(formValue.status, 10);
    const currentStatus = parseInt(registration.status || 1, 10);
    const signedContractScanUrl = toUrlString(formValue.signedContractScanUrl);
    const readiness = buildContractReadiness(formValue);

    const allowed =
      finalStatus === currentStatus ||
      (STATUS_TRANSITIONS[currentStatus] || []).includes(finalStatus);
    if (!allowed) {
      toast.error("انتقال به این وضعیت از وضعیت فعلی مجاز نیست.");
      setLoading(false);
      setSubmitting(false);
      return;
    }

    if (finalStatus === FINAL_APPROVAL_STATUS && !readiness.ready) {
      toast.error(
        `برای تایید نهایی و صدور قرارداد، این موارد ناقص است: ${readiness.missing
          .map((m) => m.label)
          .join("، ")}`
      );
      setLoading(false);
      setSubmitting(false);
      return;
    }

    if (
      finalStatus === 6 &&
      (!formValue.firstName || !formValue.lastName || !formValue.mobile || !formValue.nationalCode)
    ) {
      toast.error("برای تایید اولیه، نام، نام خانوادگی، کد ملی و موبایل الزامی است.");
      setLoading(false);
      setSubmitting(false);
      return;
    }

    // تایید نهایی دیگر به آپلود اسکن قرارداد وابسته نیست؛
    // قرارداد پس از تایید نهایی با امضای دیجیتال صادر می‌شود.

    const sendData = {
      id: parseInt(registrationId),
      status: finalStatus,
      reviewNotes: formValue.reviewNotes || "",
      firstName: formValue.firstName || "",
      lastName: formValue.lastName || "",
      nationalCode: fa2enDigits(formValue.nationalCode).trim() || "",
      mobile: fa2enDigits(formValue.mobile).trim() || "",
      email: formValue.email || "",
      birthDate: formatLocalDateForApi(formValue.birthDate),
      companyName: formValue.companyName || "",
      companyRegistrationNumber: formValue.companyRegistrationNumber || "",
      companyNationalId: fa2enDigits(formValue.companyNationalId).trim() || "",
      companyPhone: fa2enDigits(formValue.companyPhone).trim() || "",
      ibanNumber: normalizeIban(formValue.ibanNumber) || "",
      bankAccountNumber: onlyDigits(formValue.bankAccountNumber) || "",
      cardNumber: onlyDigits(formValue.cardNumber) || "",
      companyWebsite: formValue.companyWebsite || "",
      companyAddress: formValue.companyAddress || "",
      city: formValue.city || "",
      cityId: formValue.cityId ? Number(formValue.cityId) : null,
      province: formValue.province || "",
      provinceId: formValue.provinceId ? Number(formValue.provinceId) : null,
      officialRegistrationDocumentsUrl: toUrlString(
        formValue.officialRegistrationDocumentsUrl
      ),
      authorizedSignatoriesDocumentUrl: toUrlString(
        formValue.authorizedSignatoriesDocumentUrl
      ),
      signedContractScanUrl: signedContractScanUrl,
      postalCode: fa2enDigits(formValue.postalCode).trim() || "",
      workExperience: formValue.workExperience || "",
      foundedYear:
        formValue.registrationType === "legal" && formValue.foundedYear
          ? parseInt(formValue.foundedYear)
          : null,
      employeeCount: formValue.employeeCount
        ? parseInt(formValue.employeeCount)
        : null,
      canPerformSiteVisit: !!formValue.canPerformSiteVisit,
      isSatbaApproved: !!formValue.isSatbaApproved,
      distributionCompanyIds: formValue.isInDistributionVendorList
        ? formValue.distributionCompanyIds || []
        : [],
    };
    // هنگام تایید (وضعیت 3)، موبایل پیمانکار را صریحاً بفرست تا بک‌اند SMS را به همان شماره بفرستد (نه به متقاضی یا شماره اشتباه)
    if (ACCOUNT_PROVISION_STATUSES.has(finalStatus) && formValue.mobile) {
      sendData.contractorMobile = fa2enDigits(formValue.mobile).trim();
    }

    try {
      const res = await DefaultApiService.postApi(
        `${apiBase}/UpdateStatus`,
        sendData
      );

      if (res.isSuccess) {
        toast.success(res.message || "بروزرسانی با موفقیت انجام شد");
        onNavigateToListPage();
      } else {
        toast.error(res.message || "خطا در بروزرسانی");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "خطایی رخ داده است.");
    } finally {
      setLoading(false);
      setSubmitting(false);
      setConfirmOpen(false);
      setPendingAction(null);
    }
  };

  const openStatusConfirm = (nextStatus, values, setSubmitting) => {
    const current = Number(registration.status || 1);
    const next = Number(nextStatus);
    if (next !== current && !(STATUS_TRANSITIONS[current] || []).includes(next)) {
      toast.error("این تغییر وضعیت مجاز نیست.");
      return;
    }
    setPendingAction({
      nextStatus: next,
      keepStatusOnly: next === current,
      values,
      setSubmitting,
    });
    setConfirmOpen(true);
  };

  const handleConfirmAction = () => {
    if (!pendingAction) return;
    const values = { ...pendingAction.values, status: pendingAction.nextStatus };
    onSubmit(values, {
      setSubmitting: pendingAction.setSubmitting || (() => {}),
    });
  };

  const handleCloseConfirm = () => {
    if (loading) return;
    setConfirmOpen(false);
    setPendingAction(null);
  };

  const currentStatusMeta = getStatusMeta(registration.status || 1);
  const allowedStatusActions = getAllowedStatusActions(registration.status || 1);
  const pendingToMeta = pendingAction
    ? getStatusMeta(pendingAction.nextStatus)
    : null;

  return (
    <Grid container spacing={2.5}>
      <Grid item xs={12}>
        <BreadcrumbsComponent />
      </Grid>

      {/* Header */}
      <Grid item xs={12}>
        <Card
          sx={{
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                <Avatar
                  sx={{
                    width: 84,
                    height: 84,
                    bgcolor: "primary.50",
                    color: "primary.main",
                    border: "2px solid",
                    borderColor: "divider",
                    "& img": {
                      objectFit: "cover",
                    },
                  }}
                  src={
                    registration.registrationType === "legal"
                      ? registration.companyLogoUrl
                        ? `${baseUrl}${registration.companyLogoUrl}`
                        : undefined
                      : registration.profileImageUrl
                      ? `${baseUrl}${registration.profileImageUrl}`
                      : undefined
                  }
                >
                  {!registration.companyLogoUrl &&
                    !registration.profileImageUrl && (
                      <PersonIcon sx={{ fontSize: 60 }} />
                    )}
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight="bold">
                    بررسی درخواست پیمانکار
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary">
                    #{registration.id || registrationId}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {displayName} - {displayCompanyName}
                  </Typography>
                  <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Chip
                      label={
                        statusOptions.find(
                          (s) => s.value === registration.status
                        )?.label || "نامشخص"
                      }
                      color={statusColors[registration.status] || "default"}
                      sx={{ fontWeight: 700 }}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Account: ${
                        operationalAccess?.accountStatusTitle ||
                        operationalAccess?.AccountStatusTitle ||
                        (registration.status === 3
                          ? "FinalApproved"
                          : registration.status === 6
                          ? "InitialApproved"
                          : registration.status === 5
                          ? "AwaitingDocuments"
                          : registration.status === 2
                          ? "DocumentsReview"
                          : registration.status === 4
                          ? "Rejected"
                          : "Registered")
                      }`}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      color="info"
                      label={`Contract: ${
                        operationalAccess?.contractStatusTitle ||
                        operationalAccess?.ContractStatusTitle ||
                        "—"
                      }`}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      color="secondary"
                      label={`Guarantee: ${
                        operationalAccess?.guaranteeStatusTitle ||
                        operationalAccess?.GuaranteeStatusTitle ||
                        "—"
                      }`}
                    />
                    <Chip
                      size="small"
                      color={
                        operationalAccess?.operationalAccessEnabled ||
                        operationalAccess?.OperationalAccessEnabled
                          ? "success"
                          : "warning"
                      }
                      label={`Operational Access: ${
                        operationalAccess?.operationalAccessEnabled ||
                        operationalAccess?.OperationalAccessEnabled
                          ? "Enabled"
                          : "Disabled"
                      }`}
                    />
                    {(
                      operationalAccess?.blockedReasons ||
                      operationalAccess?.BlockedReasons ||
                      []
                    ).length > 0 && (
                      <Chip
                        size="small"
                        color="error"
                        variant="outlined"
                        label={
                          (
                            operationalAccess?.blockedReasons ||
                            operationalAccess?.BlockedReasons
                          )[0]
                        }
                      />
                    )}
                    {registration.createOn && (
                      <Chip
                        icon={<CalendarTodayIcon />}
                        label={`ثبت: ${toPersianDate(registration.createOn)}`}
                        variant="outlined"
                      />
                    )}
                  </Box>
                  {needsMasterContractIssue ? (
                    <Alert
                      severity="warning"
                      sx={{ mt: 1.5 }}
                      action={
                        <Button
                          color="inherit"
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            navigate(
                              `/SLN/Management/ContractorMasterContract?contractorInfoId=${contractorInfoId}`
                            )
                          }
                        >
                          صدور قرارداد
                        </Button>
                      }
                    >
                      تایید نهایی انجام شده، اما قرارداد هنوز صادر نشده است.
                      برای ادامه فرایند امضا/ضمانت، قرارداد را صادر کنید.
                    </Alert>
                  ) : null}
                  {masterContractId ? (
                    <Alert
                      severity="success"
                      sx={{ mt: 1.5 }}
                      action={
                        <Button
                          color="inherit"
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            navigate(
                              `/SLN/Management/ContractorMasterContract?contractId=${masterContractId}`
                            )
                          }
                        >
                          مشاهده قرارداد
                        </Button>
                      }
                    >
                      قرارداد صادر شده است — ادامه امضا و بررسی ضمانت از کنسول
                      قرارداد.
                    </Alert>
                  ) : null}
                </Box>
              </Box>
              <Tooltip title="بازگشت به لیست">
                <IconButton
                  onClick={onNavigateToListPage}
                  sx={{
                    bgcolor: "background.paper",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Formik
              enableReinitialize
              initialValues={{
                status: registration.status || 1,
                reviewNotes: registration.reviewNotes || "",
                registrationType: registration.registrationType || "individual",
                firstName: registration.firstName || "",
                lastName: registration.lastName || "",
                nationalCode: registration.nationalCode || "",
                mobile: registration.mobile || "",
                email: registration.email || "",
                birthDate: toDateInputValue(registration.birthDate),
                companyName: registration.companyName || "",
                companyRegistrationNumber:
                  registration.companyRegistrationNumber || "",
                companyNationalId: registration.companyNationalId || "",
                companyPhone: registration.companyPhone || "",
                ibanNumber: registration.ibanNumber || "",
                bankAccountNumber: registration.bankAccountNumber || "",
                cardNumber: registration.cardNumber || "",
                companyWebsite: registration.companyWebsite || "",
                companyAddress: registration.companyAddress || "",
                officialRegistrationDocumentsUrl:
                  registration.officialRegistrationDocumentsUrl || "",
                authorizedSignatoriesDocumentUrl:
                  registration.authorizedSignatoriesDocumentUrl || "",
                signedContractScanUrl: registration.signedContractScanUrl || "",
                city: registration.city || "",
                province: registration.province || "",
                provinceId: initialProvinceId,
                cityId: initialCityId,
                postalCode: registration.postalCode || "",
                workExperience: registration.workExperience || "",
                foundedYear: registration.foundedYear || "",
                employeeCount: registration.employeeCount || "",
                canPerformSiteVisit: !!registration.canPerformSiteVisit,
                isSatbaApproved: !!registration.isSatbaApproved,
                isInDistributionVendorList:
                  Array.isArray(registration.distributionCompanyIds) &&
                  registration.distributionCompanyIds.length > 0,
                distributionCompanyIds: Array.isArray(
                  registration.distributionCompanyIds
                )
                  ? registration.distributionCompanyIds.map(Number)
                  : [],
              }}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                isSubmitting,
                setFieldValue,
                setSubmitting,
                validateForm,
                setTouched,
              }) => {
                const onMakeFormikVariable = (name) => ({
                  name: name,
                  fullWidth: true,
                  value: values[name],
                  error: errors[name] && touched[name],
                  helperText: errors[name] && touched[name] && errors[name],
                  variant: "outlined",
                });

                const isLegal = values.registrationType === "legal";
                const contractReadiness = buildContractReadiness(values);
                const intendedStatus = pendingAction
                  ? Number(pendingAction.nextStatus)
                  : Number(registration.status || values.status || 1);
                const provisioningSelected =
                  ACCOUNT_PROVISION_STATUSES.has(intendedStatus);
                const finalApprovalSelected =
                  intendedStatus === FINAL_APPROVAL_STATUS;

                const requestAction = async (nextStatus) => {
                  const formErrors = await validateForm();
                  if (formErrors && Object.keys(formErrors).length > 0) {
                    setTouched(
                      Object.keys(formErrors).reduce((acc, key) => {
                        acc[key] = true;
                        return acc;
                      }, {})
                    );
                    toast.error("لطفا خطاهای فرم را برطرف کنید.");
                    return;
                  }
                  openStatusConfirm(nextStatus, values, setSubmitting);
                };

                return (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                    }}
                  >                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Alert
                          severity={contractReadiness.ready ? "success" : "warning"}
                          sx={{ mb: 1 }}
                        >
                          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                            آمادگی صدور قرارداد پیمانکار
                          </Typography>
                          {contractReadiness.ready ? (
                            <Typography variant="body2">
                              اطلاعات هویتی، آدرس و بانکی برای صدور قرارداد کامل است. تایید
                              نهایی (۳) امضای قرارداد را باز می‌کند.
                            </Typography>
                          ) : (
                            <Typography variant="body2">
                              موارد ناقص برای تایید نهایی:{" "}
                              {contractReadiness.missing.map((m) => m.label).join("، ")}
                              {finalApprovalSelected
                                ? " — تا تکمیل این موارد نمی‌توانید تایید نهایی کنید."
                                : provisioningSelected
                                ? " — تایید اولیه (۶) با حداقل هویت/تماس ممکن است."
                                : ""}
                            </Typography>
                          )}
                          <Box
                            sx={{
                              mt: 1.5,
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.75,
                            }}
                          >
                            {contractReadiness.checks.map((item) => (
                              <Chip
                                key={item.key}
                                size="small"
                                label={item.label}
                                color={item.ok ? "success" : "default"}
                                variant={item.ok ? "filled" : "outlined"}
                                icon={
                                  item.ok ? (
                                    <CheckCircleIcon fontSize="small" />
                                  ) : (
                                    <CancelIcon fontSize="small" />
                                  )
                                }
                              />
                            ))}
                          </Box>
                        </Alert>
                      </Grid>

                      <Grid item xs={12}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            mb: 1,
                            gap: 1,
                          }}
                        >
                          <Typography variant="h6" fontWeight={700}>
                            اطلاعات درخواست
                            {isLegal ? " (حقوقی)" : " (حقیقی)"}
                          </Typography>
                          {!canEditDetails && (
                            <Chip
                              color="warning"
                              size="small"
                              label="به دلیل تایید نهایی، ویرایش اطلاعات غیرفعال است"
                            />
                          )}
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField
                          label={isLegal ? "نام نماینده *" : "نام *"}
                          onChange={wrapFormikHandleChange(handleChange)}
                          onBlur={handleBlur}
                          disabled={!canEditDetails}
                          {...onMakeFormikVariable("firstName")}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label={isLegal ? "نام خانوادگی نماینده *" : "نام خانوادگی *"}
                          onChange={wrapFormikHandleChange(handleChange)}
                          onBlur={handleBlur}
                          disabled={!canEditDetails}
                          {...onMakeFormikVariable("lastName")}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="کد ملی *"
                          onChange={(event) =>
                            setFieldValue(
                              "nationalCode",
                              onlyDigits(fa2enDigits(event.target.value)).slice(0, 10)
                            )
                          }
                          onBlur={handleBlur}
                          disabled={!canEditDetails}
                          inputProps={{ maxLength: 10, inputMode: "numeric" }}
                          {...onMakeFormikVariable("nationalCode")}
                          helperText={
                            (errors.nationalCode &&
                              touched.nationalCode &&
                              errors.nationalCode) ||
                            "۱۰ رقم"
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="موبایل *"
                          onChange={(event) =>
                            setFieldValue(
                              "mobile",
                              onlyDigits(fa2enDigits(event.target.value)).slice(0, 11)
                            )
                          }
                          onBlur={handleBlur}
                          disabled={!canEditDetails}
                          inputProps={{ maxLength: 11, inputMode: "tel" }}
                          {...onMakeFormikVariable("mobile")}
                          helperText={
                            (errors.mobile && touched.mobile && errors.mobile) ||
                            "مثال: 09123456789"
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="ایمیل *"
                          onChange={wrapFormikHandleChange(handleChange)}
                          onBlur={handleBlur}
                          disabled={!canEditDetails}
                          {...onMakeFormikVariable("email")}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <JalaliDatePicker
                          label={
                            values.registrationType === "legal"
                              ? "تاریخ تولد نماینده شرکت *"
                              : "تاریخ تولد *"
                          }
                          onChange={(date) => setFieldValue("birthDate", date)}
                          disabled={!canEditDetails}
                          {...onMakeFormikVariable("birthDate")}
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField
                          label={isLegal ? "نام شرکت *" : "نام شرکت (در صورت وجود)"}
                          onChange={wrapFormikHandleChange(handleChange)}
                          onBlur={handleBlur}
                          disabled={!canEditDetails}
                          {...onMakeFormikVariable("companyName")}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label={isLegal ? "شماره ثبت شرکت *" : "شماره ثبت شرکت"}
                          onChange={(event) =>
                            setFieldValue(
                              "companyRegistrationNumber",
                              onlyDigits(fa2enDigits(event.target.value)).slice(0, 6)
                            )
                          }
                          onBlur={handleBlur}
                          disabled={!canEditDetails}
                          inputProps={{ maxLength: 6, inputMode: "numeric" }}
                          {...onMakeFormikVariable("companyRegistrationNumber")}
                          helperText={
                            (errors.companyRegistrationNumber &&
                              touched.companyRegistrationNumber &&
                              errors.companyRegistrationNumber) ||
                            "حداکثر ۶ رقم"
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label={isLegal ? "شناسه ملی شرکت *" : "شناسه ملی شرکت"}
                          onChange={(event) =>
                            setFieldValue(
                              "companyNationalId",
                              onlyDigits(fa2enDigits(event.target.value)).slice(
                                0,
                                COMPANY_NATIONAL_ID_LENGTH
                              )
                            )
                          }
                          onBlur={handleBlur}
                          disabled={!canEditDetails}
                          inputProps={companyNationalIdInputProps}
                          {...onMakeFormikVariable("companyNationalId")}
                          helperText={
                            (errors.companyNationalId &&
                              touched.companyNationalId &&
                              errors.companyNationalId) ||
                            COMPANY_NATIONAL_ID_HINT_MSG
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label={isLegal ? "تلفن شرکت *" : "تلفن شرکت"}
                          onChange={(event) =>
                            setFieldValue(
                              "companyPhone",
                              onlyDigits(fa2enDigits(event.target.value)).slice(0, 11)
                            )
                          }
                          onBlur={handleBlur}
                          disabled={!canEditDetails}
                          inputProps={{ maxLength: 11, inputMode: "tel" }}
                          {...onMakeFormikVariable("companyPhone")}
                          helperText={
                            (errors.companyPhone &&
                              touched.companyPhone &&
                              errors.companyPhone) ||
                            (isLegal ? "۱۱ رقم، با ۰ شروع شود" : "")
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="شماره شبا *"
                          onChange={(event) =>
                            setFieldValue(
                              "ibanNumber",
                              normalizeIban(event.target.value)
                            )
                          }
                          onBlur={handleBlur}
                          disabled={!canEditDetails}
                          inputProps={{ maxLength: 26 }}
                          {...onMakeFormikVariable("ibanNumber")}
                          helperText={
                            (errors.ibanNumber &&
                              touched.ibanNumber &&
                              errors.ibanNumber) ||
                            "IR + ۲۴ رقم"
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="شماره حساب *"
                          onChange={(event) =>
                            setFieldValue(
                              "bankAccountNumber",
                              onlyDigits(event.target.value)
                            )
                          }
                          onBlur={handleBlur}
                          disabled={!canEditDetails}
                          inputProps={{ maxLength: 20, inputMode: "numeric" }}
                          {...onMakeFormikVariable("bankAccountNumber")}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="شماره کارت *"
                          onChange={(event) =>
                            setFieldValue(
                              "cardNumber",
                              onlyDigits(event.target.value)
                            )
                          }
                          onBlur={handleBlur}
                          disabled={!canEditDetails}
                          inputProps={{ maxLength: 16, inputMode: "numeric" }}
                          {...onMakeFormikVariable("cardNumber")}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="وب‌سایت"
                          onChange={wrapFormikHandleChange(handleChange)}
                          onBlur={handleBlur}
                          disabled={!canEditDetails}
                          {...onMakeFormikVariable("companyWebsite")}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <DropdownPicker
                          label="استان *"
                          onChange={(event) => {
                            const selectedProvinceId = Number(event.target.value) || "";
                            const selectedProvince = provinceOptions.find(
                              (item) => item.id === selectedProvinceId
                            );
                            setFieldValue("provinceId", selectedProvinceId);
                            setFieldValue("province", selectedProvince?.title || "");
                            setFieldValue("city", "");
                            setFieldValue("cityId", "");
                          }}
                          disabled={!canEditDetails}
                          list={provinceOptions}
                          optionName="title"
                          optionValue="id"
                          {...onMakeFormikVariable("provinceId")}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <DropdownPicker
                          label="شهر *"
                          onChange={(event) => {
                            const selectedCityId = Number(event.target.value) || "";
                            const selectedCity = cityOptions.find(
                              (city) => city.id === selectedCityId
                            );
                            setFieldValue("cityId", selectedCityId);
                            setFieldValue("city", selectedCity?.cityName || "");
                          }}
                          list={cityOptions
                            .filter(
                              (city) =>
                                !values.provinceId ||
                                Number(city.provinceId) === Number(values.provinceId)
                            )
                            .map((city) => ({
                              id: city.id,
                              title: city.cityName,
                            }))}
                          optionName="title"
                          optionValue="id"
                          disabled={!canEditDetails || !values.provinceId}
                          {...onMakeFormikVariable("cityId")}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="کدپستی *"
                          onChange={(event) =>
                            setFieldValue(
                              "postalCode",
                              onlyDigits(fa2enDigits(event.target.value)).slice(0, 10)
                            )
                          }
                          onBlur={handleBlur}
                          disabled={!canEditDetails}
                          inputProps={{ maxLength: 10, inputMode: "numeric" }}
                          {...onMakeFormikVariable("postalCode")}
                          helperText={
                            (errors.postalCode &&
                              touched.postalCode &&
                              errors.postalCode) ||
                            "۱۰ رقم"
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="سابقه کار *"
                          onChange={wrapFormikHandleChange(handleChange)}
                          onBlur={handleBlur}
                          disabled={!canEditDetails}
                          {...onMakeFormikVariable("workExperience")}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label={isLegal ? "سال تاسیس *" : "سال تاسیس"}
                          type="number"
                          onChange={wrapFormikHandleChange(handleChange)}
                          onBlur={handleBlur}
                          disabled={
                            !canEditDetails || values.registrationType !== "legal"
                          }
                          inputProps={{ min: 1200, max: 1600 }}
                          placeholder={
                            values.registrationType === "legal"
                              ? "مثال: ۱۴۰۱"
                              : "فقط برای شخص حقوقی"
                          }
                          {...onMakeFormikVariable("foundedYear")}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          label="تعداد کارکنان"
                          type="number"
                          onChange={wrapFormikHandleChange(handleChange)}
                          onBlur={handleBlur}
                          disabled={!canEditDetails}
                          {...onMakeFormikVariable("employeeCount")}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label={isLegal ? "آدرس شرکت *" : "آدرس *"}
                          multiline
                          rows={2}
                          onChange={wrapFormikHandleChange(handleChange)}
                          onBlur={handleBlur}
                          disabled={!canEditDetails}
                          inputProps={{ maxLength: 200 }}
                          {...onMakeFormikVariable("companyAddress")}
                          helperText={
                            (errors.companyAddress &&
                              touched.companyAddress &&
                              errors.companyAddress) ||
                            "حداقل ۱۰ و حداکثر ۲۰۰ کاراکتر — در قرارداد درج می‌شود"
                          }
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={values.canPerformSiteVisit}
                              onChange={(event) =>
                                setFieldValue(
                                  "canPerformSiteVisit",
                                  event.target.checked
                                )
                              }
                              disabled={!canEditDetails}
                            />
                          }
                          label="آمادگی انجام بازدید توسط پیمانکار"
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 2.5,
                            borderRadius: 2,
                            bgcolor: "background.default",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            sx={{
                              mb: 0.5,
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <VerifiedIcon color="primary" fontSize="small" />
                            تأییدیه‌ها و عضویت در فهرست‌های رسمی
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                          >
                            وضعیت عضویت پیمانکار در فهرست‌های رسمی ساتبا و
                            شرکت‌های توزیع برق.
                          </Typography>

                          <FormGroup>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!values.isSatbaApproved}
                                  onChange={(event) =>
                                    setFieldValue(
                                      "isSatbaApproved",
                                      event.target.checked
                                    )
                                  }
                                  disabled={!canEditDetails}
                                />
                              }
                              label="در لیست پیمانکاران مورد تایید ساتبا هست"
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!values.isInDistributionVendorList}
                                  onChange={(event) => {
                                    const checked = event.target.checked;
                                    setFieldValue(
                                      "isInDistributionVendorList",
                                      checked
                                    );
                                    if (!checked) {
                                      setFieldValue("distributionCompanyIds", []);
                                    }
                                  }}
                                  disabled={!canEditDetails}
                                />
                              }
                              label="در وندور لیست شرکت‌های توزیع برق هست"
                            />
                          </FormGroup>

                          <Collapse
                            in={!!values.isInDistributionVendorList}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box
                              sx={{
                                mt: 2,
                                p: 2,
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: "primary.light",
                                bgcolor: (theme) =>
                                  theme.palette.mode === "dark"
                                    ? "rgba(25, 118, 210, 0.08)"
                                    : "rgba(25, 118, 210, 0.04)",
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                fontWeight={700}
                                sx={{
                                  mb: 1,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <ElectricBoltIcon
                                  fontSize="small"
                                  color="warning"
                                />
                                انتخاب شرکت‌های توزیع
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 1.5 }}
                              >
                                شرکت‌هایی که پیمانکار در وندور لیست آن‌ها قرار دارد
                                را انتخاب کنید.
                              </Typography>

                              {distributionCompanies.length === 0 ? (
                                <Alert severity="warning">
                                  لیست شرکت‌های توزیع در دسترس نیست.
                                </Alert>
                              ) : (
                                <>
                                  <Box
                                    sx={{
                                      maxHeight: 280,
                                      overflowY: "auto",
                                      pr: 1,
                                      display: "grid",
                                      gridTemplateColumns: {
                                        xs: "1fr",
                                        sm: "1fr 1fr",
                                      },
                                      gap: 0.5,
                                    }}
                                  >
                                    {distributionCompanies.map((company) => {
                                      const companyId = Number(
                                        company.id ?? company.Id
                                      );
                                      const title =
                                        company.title || company.Title || "";
                                      const selected = (
                                        values.distributionCompanyIds || []
                                      ).includes(companyId);
                                      return (
                                        <FormControlLabel
                                          key={companyId}
                                          control={
                                            <Checkbox
                                              size="small"
                                              checked={selected}
                                              disabled={!canEditDetails}
                                              onChange={(event) => {
                                                const checked =
                                                  event.target.checked;
                                                const current =
                                                  values.distributionCompanyIds ||
                                                  [];
                                                const next = checked
                                                  ? [...current, companyId]
                                                  : current.filter(
                                                      (id) => id !== companyId
                                                    );
                                                setFieldValue(
                                                  "distributionCompanyIds",
                                                  next
                                                );
                                              }}
                                            />
                                          }
                                          label={
                                            <Typography variant="body2">
                                              {title}
                                            </Typography>
                                          }
                                        />
                                      );
                                    })}
                                  </Box>
                                  {(values.distributionCompanyIds || [])
                                    .length > 0 && (
                                    <Box
                                      sx={{
                                        mt: 1.5,
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 0.75,
                                        alignItems: "center",
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        انتخاب‌شده (
                                        {
                                          values.distributionCompanyIds.length
                                        }
                                        ):
                                      </Typography>
                                      {values.distributionCompanyIds.map(
                                        (id) => {
                                          const company =
                                            distributionCompanies.find(
                                              (c) =>
                                                Number(c.id ?? c.Id) ===
                                                Number(id)
                                            );
                                          return (
                                            <Chip
                                              key={id}
                                              size="small"
                                              label={
                                                company?.title ||
                                                company?.Title ||
                                                id
                                              }
                                              onDelete={
                                                canEditDetails
                                                  ? () =>
                                                      setFieldValue(
                                                        "distributionCompanyIds",
                                                        (
                                                          values.distributionCompanyIds ||
                                                          []
                                                        ).filter(
                                                          (x) => x !== id
                                                        )
                                                      )
                                                  : undefined
                                              }
                                            />
                                          );
                                        }
                                      )}
                                    </Box>
                                  )}
                                </>
                              )}
                            </Box>
                          </Collapse>
                        </Paper>
                      </Grid>

                      <Grid item xs={12}>
                        <Divider sx={{ my: 0.5 }} />
                      </Grid>

                      <Grid item xs={12}>
                        <Typography
                          variant="h6"
                          fontWeight={700}
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <AttachFileIcon />
                          اسناد و مدارک ارسالی
                        </Typography>
                      </Grid>

                      {values.registrationType === "legal" && (
                      <Grid item xs={12} sm={6} md={3}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            تصویر کارت ملی
                          </Typography>
                          <Chip
                            label={
                              registration.nationalCardImageUrl
                                ? "آپلود شده"
                                : "آپلود نشده"
                            }
                            color={
                              registration.nationalCardImageUrl ? "success" : "error"
                            }
                            size="small"
                            sx={{ mb: 1.5 }}
                          />
                          <Box sx={{ display: "flex", gap: 1 }}>
                            {registration.nationalCardImageUrl ? (
                              <>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<VisibilityIcon />}
                                  href={`${baseUrl}${registration.nationalCardImageUrl}`}
                                  target="_blank"
                                >
                                  مشاهده
                                </Button>
                                <IconButton
                                  size="small"
                                  href={`${baseUrl}${registration.nationalCardImageUrl}`}
                                  download
                                >
                                  <DownloadIcon />
                                </IconButton>
                              </>
                            ) : (
                              <CancelIcon color="error" />
                            )}
                          </Box>
                        </Paper>
                      </Grid>
                      )}

                      {values.registrationType === "legal" && (
                      <Grid item xs={12} sm={6} md={3}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            مدارک ثبتی شرکت
                          </Typography>
                          <Chip
                            label={
                              registration.officialRegistrationDocumentsUrl
                                ? "آپلود شده"
                                : "آپلود نشده"
                            }
                            color={
                              registration.officialRegistrationDocumentsUrl
                                ? "success"
                                : "error"
                            }
                            size="small"
                            sx={{ mb: 1.5 }}
                          />
                          <Box sx={{ display: "flex", gap: 1 }}>
                            {registration.officialRegistrationDocumentsUrl ? (
                              <>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<VisibilityIcon />}
                                  href={`${baseUrl}${registration.officialRegistrationDocumentsUrl}`}
                                  target="_blank"
                                >
                                  مشاهده
                                </Button>
                                <IconButton
                                  size="small"
                                  href={`${baseUrl}${registration.officialRegistrationDocumentsUrl}`}
                                  download
                                >
                                  <DownloadIcon />
                                </IconButton>
                              </>
                            ) : (
                              <CancelIcon color="error" />
                            )}
                          </Box>
                        </Paper>
                      </Grid>
                      )}

                      <Grid item xs={12} sm={6} md={3}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            مدارک صاحبان امضا
                          </Typography>
                          <Chip
                            label={
                              registration.authorizedSignatoriesDocumentUrl
                                ? "آپلود شده"
                                : "آپلود نشده"
                            }
                            color={
                              registration.authorizedSignatoriesDocumentUrl
                                ? "success"
                                : "error"
                            }
                            size="small"
                            sx={{ mb: 1.5 }}
                          />
                          <Box sx={{ display: "flex", gap: 1 }}>
                            {registration.authorizedSignatoriesDocumentUrl ? (
                              <>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<VisibilityIcon />}
                                  href={`${baseUrl}${registration.authorizedSignatoriesDocumentUrl}`}
                                  target="_blank"
                                >
                                  مشاهده
                                </Button>
                                <IconButton
                                  size="small"
                                  href={`${baseUrl}${registration.authorizedSignatoriesDocumentUrl}`}
                                  download
                                >
                                  <DownloadIcon />
                                </IconButton>
                              </>
                            ) : (
                              <CancelIcon color="error" />
                            )}
                          </Box>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} sm={6} md={3}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            مجوز شرکت
                          </Typography>
                          <Chip
                            label={
                              registration.companyLicenseImageUrl
                                ? "آپلود شده"
                                : "آپلود نشده"
                            }
                            color={
                              registration.companyLicenseImageUrl
                                ? "success"
                                : "error"
                            }
                            size="small"
                            sx={{ mb: 1.5 }}
                          />
                          <Box sx={{ display: "flex", gap: 1 }}>
                            {registration.companyLicenseImageUrl ? (
                              <>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<VisibilityIcon />}
                                  href={`${baseUrl}${registration.companyLicenseImageUrl}`}
                                  target="_blank"
                                >
                                  مشاهده
                                </Button>
                                <IconButton
                                  size="small"
                                  href={`${baseUrl}${registration.companyLicenseImageUrl}`}
                                  download
                                >
                                  <DownloadIcon />
                                </IconButton>
                              </>
                            ) : (
                              <CancelIcon color="error" />
                            )}
                          </Box>
                        </Paper>
                      </Grid>

                      <Grid item xs={12} sm={6} md={6}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            نمونه کارها
                          </Typography>
                          <Chip
                            label={
                              workSampleUrls.length > 0
                                ? `${workSampleUrls.length} فایل`
                                : "آپلود نشده"
                            }
                            color={workSampleUrls.length > 0 ? "success" : "error"}
                            size="small"
                            sx={{ mb: 1.5 }}
                          />
                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            {workSampleUrls.length > 0 ? (
                              workSampleUrls.map((url, index) => (
                                <Button
                                  key={url || index}
                                  size="small"
                                  variant="outlined"
                                  startIcon={<VisibilityIcon />}
                                  href={`${baseUrl}${url}`}
                                  target="_blank"
                                >
                                  فایل {index + 1}
                                </Button>
                              ))
                            ) : (
                              <CancelIcon color="error" />
                            )}
                          </Box>
                        </Paper>
                      </Grid>

                      <Grid item xs={12}>
                        <Divider sx={{ my: 0.5 }} />
                      </Grid>

                      <Grid item xs={12}>
                        <Alert severity="info" sx={{ mb: 0.5 }}>
                          پس از تایید نهایی، قرارداد به‌صورت دیجیتال صادر و از پنل پیمانکار/اپراتور امضا می‌شود؛ آپلود اسکن قرارداد لازم نیست.
                        </Alert>
                      </Grid>

                      <Grid item xs={12}>
                        <Paper
                          variant="outlined"
                          sx={{ p: 2, borderRadius: 2, bgcolor: "action.hover" }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              alignItems: "center",
                              gap: 1.5,
                              mb: 1.5,
                            }}
                          >
                            <Typography variant="subtitle2" fontWeight={700}>
                              وضعیت فعلی:
                            </Typography>
                            <Chip
                              label={currentStatusMeta.label}
                              color={statusColors[currentStatusMeta.value] || "default"}
                              size="small"
                            />
                          </Box>

                          {allowedStatusActions.length > 0 ? (
                            <>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 1.5 }}
                              >
                                فقط اقدامات مجاز از این وضعیت نمایش داده می‌شود. قبل از اعمال، تأیید دوباره لازم است.
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 1,
                                }}
                              >
                                {allowedStatusActions.map((action) => (
                                  <Button
                                    key={action.value}
                                    variant="contained"
                                    color={action.color || "primary"}
                                    disabled={loading || isSubmitting}
                                    onClick={() => requestAction(action.value)}
                                  >
                                    {action.buttonLabel || action.label}
                                  </Button>
                                ))}
                              </Box>
                            </>
                          ) : (
                            <Alert severity="info" sx={{ mt: 0.5 }}>
                              برای این وضعیت اقدام تغییر وضعیت دیگری مجاز نیست.
                            </Alert>
                          )}
                        </Paper>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label={reviewNotesLabel}
                          onChange={wrapFormikHandleChange(handleChange)}
                          onBlur={handleBlur}
                          multiline
                          rows={4}
                          placeholder="توضیحات خود را در مورد این درخواست وارد کنید..."
                          {...onMakeFormikVariable("reviewNotes")}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            justifyContent: "flex-end",
                            flexWrap: "wrap",
                            pt: 2,
                          }}
                        >
                          <Button
                            variant="outlined"
                            size="large"
                            onClick={onNavigateToListPage}
                            disabled={loading}
                            startIcon={<ArrowBackIcon />}
                          >
                            بازگشت به لیست
                          </Button>
                          {canEditDetails && (
                            <Button
                              variant="outlined"
                              color="primary"
                              size="large"
                              disabled={loading || isSubmitting}
                              onClick={() =>
                                requestAction(Number(registration.status || 1))
                              }
                              startIcon={
                                loading ? (
                                  <CircularProgress size={20} color="inherit" />
                                ) : (
                                  <CheckCircleIcon />
                                )
                              }
                            >
                              ذخیره اطلاعات (بدون تغییر وضعیت)
                            </Button>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </form>
                );
              }}
            </Formik>
          </CardContent>
        </Card>
      </Grid>

      <Dialog
        open={confirmOpen}
        onClose={handleCloseConfirm}
        maxWidth="sm"
        fullWidth
        dir="rtl"
      >
        <DialogTitle>
          {pendingAction?.keepStatusOnly
            ? "تأیید ذخیره اطلاعات"
            : "تأیید تغییر وضعیت"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div" sx={{ color: "text.primary" }}>
            {pendingAction?.keepStatusOnly ? (
              <Typography>
                اطلاعات درخواست با وضعیت فعلی{" "}
                <Chip
                  size="small"
                  label={currentStatusMeta.label}
                  color={statusColors[currentStatusMeta.value] || "default"}
                  sx={{ mx: 0.5 }}
                />{" "}
                ذخیره می‌شود. آیا مطمئن هستید؟
              </Typography>
            ) : (
              <>
                <Typography sx={{ mb: 1.5 }}>
                  آیا از تغییر وضعیت زیر اطمینان دارید؟
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    از
                  </Typography>
                  <Chip
                    label={currentStatusMeta.label}
                    color={statusColors[currentStatusMeta.value] || "default"}
                  />
                  <Typography variant="body2" color="text.secondary">
                    به
                  </Typography>
                  <Chip
                    label={pendingToMeta?.label || "-"}
                    color={
                      statusColors[pendingToMeta?.value] || "default"
                    }
                  />
                </Box>
                {(pendingAction?.nextStatus === REG_STATUS.Approved ||
                  pendingAction?.nextStatus === REG_STATUS.DocumentsApproved ||
                  pendingAction?.nextStatus === REG_STATUS.Rejected) && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    این اقدام اثر عملیاتی دارد؛ لطفاً قبل از تأیید دوباره بررسی کنید.
                  </Alert>
                )}
              </>
            )}
            {pendingAction?.values?.reviewNotes ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                یادداشت: {pendingAction.values.reviewNotes}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                یادداشت بررسی خالی است.
              </Typography>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleCloseConfirm} disabled={loading} variant="outlined">
            انصراف
          </Button>
          <Button
            onClick={handleConfirmAction}
            disabled={loading}
            variant="contained"
            color={
              pendingAction?.nextStatus === REG_STATUS.Rejected
                ? "error"
                : pendingAction?.nextStatus === REG_STATUS.Approved
                  ? "success"
                  : "primary"
            }
            startIcon={
              loading ? <CircularProgress size={18} color="inherit" /> : null
            }
          >
            {loading ? "در حال اعمال..." : "تأیید نهایی"}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default ContractorRegistrationModifyPage;
